import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';


const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission for static site
        // In production, you can integrate with a service like Formspree, EmailJS, or Netlify Forms
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsSubmitted(true);
            toast.success('Message sent successfully!');
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: MapPin,
            title: 'Visit Us',
            details: ['Bangalore, Karnataka', 'India']
        },
        {
            icon: Phone,
            title: 'Call Us',
            details: ['+91xxxxxxxxxx', 'Mon-Sat, 9AM-6PM']
        },
        {
            icon: Mail,
            title: 'Email Us',
            details: ['info@thesapa.com', 'support@thesapa.com']
        }
    ];

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="contact-page">
            {/* Hero Section */}
            <section className="section-padding pb-8">
                <div className="container-wide mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-2xl mx-auto"
                    >
                        <span className="inline-block px-4 py-1.5 bg-[#C8A86B]/10 text-[#C8A86B] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-6">
                            Get in Touch
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl text-[#4A4E4D] mb-6">
                            We'd Love to <span className="text-[#C8A86B]">Hear From You</span>
                        </h1>
                        <p className="text-[#4A4E4D]/70 text-lg leading-relaxed">
                            Have questions, suggestions, or need help finding the perfect spa? 
                            Our team is here to assist you on your wellness journey.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="py-8">
                <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {contactInfo.map((info, index) => (
                            <motion.div
                                key={info.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 border border-[#E0DAD0] text-center hover:border-[#C8A86B] transition-all duration-300"
                            >
                                <div className="w-14 h-14 mx-auto rounded-full bg-[#C8A86B]/10 flex items-center justify-center mb-4">
                                    <info.icon className="w-6 h-6 text-[#C8A86B]" />
                                </div>
                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-3">{info.title}</h3>
                                {info.details.map((detail, idx) => (
                                    <p key={idx} className="text-[#4A4E4D]/60 text-sm">{detail}</p>
                                ))}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        {/* Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#E0DAD0] shadow-sm">
                                <h2 className="font-heading text-2xl sm:text-3xl text-[#4A4E4D] mb-6">
                                    Send Us a Message
                                </h2>

                                {isSubmitted ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-center py-12"
                                    >
                                        <div className="w-16 h-16 mx-auto mb-6 bg-[#D4DED5]/50 rounded-full flex items-center justify-center">
                                            <CheckCircle className="w-8 h-8 text-[#C8A86B]" />
                                        </div>
                                        <h3 className="font-heading text-2xl text-[#4A4E4D] mb-2">Thank You!</h3>
                                        <p className="text-[#4A4E4D]/60 mb-6">
                                            We've received your message and will get back to you shortly.
                                        </p>
                                        <button
                                            onClick={() => setIsSubmitted(false)}
                                            className="btn-outline-gold"
                                            data-testid="send-another-btn"
                                        >
                                            Send Another Message
                                        </button>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-medium text-[#4A4E4D] mb-2">
                                                    Your Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="John Doe"
                                                    data-testid="input-name"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium text-[#4A4E4D] mb-2">
                                                    Email Address *
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="john@example.com"
                                                    data-testid="input-email"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="phone" className="block text-sm font-medium text-[#4A4E4D] mb-2">
                                                    Phone Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className="form-input"
                                                    placeholder="+91 98765 43210"
                                                    data-testid="input-phone"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="subject" className="block text-sm font-medium text-[#4A4E4D] mb-2">
                                                    Subject *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="subject"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="How can we help?"
                                                    data-testid="input-subject"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="message" className="block text-sm font-medium text-[#4A4E4D] mb-2">
                                                Your Message *
                                            </label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                rows={5}
                                                className="form-textarea"
                                                placeholder="Tell us more about your inquiry..."
                                                data-testid="input-message"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="btn-gold w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                            data-testid="submit-btn"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4" />
                                                    Send Message
                                                </>
                                            )}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </motion.div>

                        {/* Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="hidden lg:block"
                        >
                            <div className="relative">
                                <div className="aspect-[4/5] rounded-3xl overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=800&q=80"
                                        alt="Spa atmosphere"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="absolute -bottom-6 -right-6 bg-[#C8A86B] rounded-2xl p-6 text-white">
                                    <p className="font-heading text-2xl mb-1">24-48 Hours</p>
                                    <p className="text-white/80 text-sm">Response Time</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="section-padding bg-[#EBE6DE]/30">
                <div className="container-wide mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-3 py-1 bg-[#D4DED5]/50 text-[#4A4E4D] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                            FAQ
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D]">
                            Frequently Asked Questions
                        </h2>
                    </motion.div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {[
                            {
                                q: 'How do I book a spa appointment?',
                                a: 'You can contact the spa directly using the phone number provided on their listing page. We recommend calling ahead to check availability.'
                            },
                            {
                                q: 'Are all spas verified?',
                                a: 'Yes, we verify all spas listed on our platform for authenticity, hygiene standards, and service quality before adding them to our directory.'
                            },
                            {
                                q: 'Can I add my spa to SAPA?',
                                a: 'Absolutely! Contact us through this form with your spa details, and our team will reach out to discuss the listing process.'
                            },
                            {
                                q: 'Is there a cost to use SAPA?',
                                a: 'SAPA is completely free for users. Browse, save favorites, and discover spas at no charge.'
                            }
                        ].map((faq, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 border border-[#E0DAD0]"
                            >
                                <h3 className="font-heading text-lg text-[#4A4E4D] mb-2">{faq.q}</h3>
                                <p className="text-[#4A4E4D]/60 text-sm leading-relaxed">{faq.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
